import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRightLeft, CheckCircle2, Clock, Database, Eye, ListChecks, Loader2, Lock, Plus, RotateCcw, ScanSearch, Server, ShieldCheck, Trash2, XCircle, type LucideIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ApiError } from "@hostdock/sdk";
import type { MigrationDetail, MigrationResourceRecord, MigrationStatus } from "@hostdock/types";
import {
  Badge, Button, Checkbox, ConfirmDialog, CopyButton, cn, Dialog, ErrorState, Field, FormError, formatBytes, formatDateTime, formatRelative, Input, KeyValue, Notice, Select, Skeleton,
  Textarea, toast,
} from "@hostdock/ui";
import { api } from "../api";
import {
  actionsCell, Cell, DetailCard, EmptyCard, FilterTabs, PageHero, Pill, type PILL_TONES, primaryButtonClass, rowPrimaryClass, SearchBox, secondaryButtonClass, STAT_TILES, StatCard, tableCell, TableShell,
  Toolbar,
} from "../components/page-kit";
import { AccountReportExtras, AccountSelectionStep } from "./migrate-account";
import { CapabilityList, OTHER, OtherSource, PanelConnect, SourcePicker, UnavailableSource } from "./migrate-connect";

const ACTIVE: MigrationStatus[] = ["DRAFT", "DISCOVERING", "QUEUED", "CONNECTING", "EXPORTING", "TRANSFERRING", "RESTORING", "VALIDATING"];
const TERMINAL: MigrationStatus[] = ["COMPLETED", "COMPLETED_WITH_WARNINGS", "FAILED", "CANCELLED"];

/** Labels follow the migration job's lifecycle: Pending → Connecting → Discovering → Ready → Queued → Migrating → Verifying → done. */
const STATUS: Record<MigrationStatus, { label: string; tone: keyof typeof PILL_TONES }> = {
  DRAFT: { label: "Pending", tone: "off" }, DISCOVERING: { label: "Discovering", tone: "info" }, DISCOVERED: { label: "Ready", tone: "wait" },
  QUEUED: { label: "Queued", tone: "off" }, CONNECTING: { label: "Connecting", tone: "info" }, EXPORTING: { label: "Migrating", tone: "info" },
  TRANSFERRING: { label: "Migrating", tone: "info" }, RESTORING: { label: "Migrating", tone: "info" }, VALIDATING: { label: "Verifying", tone: "info" },
  COMPLETED: { label: "Completed", tone: "ok" }, COMPLETED_WITH_WARNINGS: { label: "Completed with warnings", tone: "wait" },
  FAILED: { label: "Failed", tone: "bad" }, CANCELLED: { label: "Cancelled", tone: "off" },
};
const StatusPill = ({ status }: { status: MigrationStatus }) => <Pill tone={STATUS[status].tone} dot>{STATUS[status].label}</Pill>;

export const KIND_LABEL: Record<string, string> = {
  DOMAIN: "Domains", SUBDOMAIN: "Subdomains", FILES: "Website files", DATABASE: "Databases", DB_USER: "Database users", EMAIL_ACCOUNT: "Email accounts",
  MAILBOX_DATA: "Mailbox contents", FORWARDER: "Forwarders", DNS_RECORD: "DNS records", CRON_JOB: "Cron jobs", REDIRECT: "Redirects", PHP_CONFIG: "PHP settings", SSL: "SSL certificates", APP_CONFIG: "Application settings",
};
const KIND_ORDER = Object.keys(KIND_LABEL);

// ---------------------------------------------------------------- list

type Filter = "all" | "running" | "done" | "failed";
const FILTER: Record<Filter, (s: MigrationStatus) => boolean> = {
  all: () => true,
  running: (s) => ACTIVE.includes(s) || s === "DISCOVERED",
  done: (s) => s === "COMPLETED" || s === "COMPLETED_WITH_WARNINGS",
  failed: (s) => s === "FAILED" || s === "CANCELLED",
};

const HOW: { icon: LucideIcon; tile: keyof typeof STAT_TILES; title: string; body: string }[] = [
  { icon: ScanSearch, tile: "teal", title: "Your panel, your account", body: "Choose the control panel you use today and sign in; HostDock shows exactly what it can move." },
  { icon: Clock, tile: "blue", title: "Runs in the background", body: "Close this page and come back: migrations keep running on their own." },
  { icon: Database, tile: "green", title: "Files & databases", body: "Website files and databases are copied across; the wizard lists what each source supports." },
  { icon: ShieldCheck, tile: "orange", title: "Read-only & safe", body: "Nothing on your old host is changed: HostDock only reads from it." },
];

export function MigrationsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const q = useQuery({ queryKey: ["migrations"], queryFn: () => api.migrations.list({ pageSize: 50 }), refetchInterval: (s) => (s.state.data?.items.some((m) => ACTIVE.includes(m.status)) ? 3000 : 20_000) });
  const items = q.data?.items;
  const count = (f: Filter) => items?.filter((m) => FILTER[f](m.status)).length ?? 0;
  const warnings = items?.filter((m) => m.status === "COMPLETED_WITH_WARNINGS").length ?? 0;
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items?.filter((m) => FILTER[filter](m.status) && (!term || m.sourceHost.toLowerCase().includes(term) || m.providerLabel.toLowerCase().includes(term) || m.targetWebsite?.domain.toLowerCase().includes(term)));
  }, [items, filter, search]);
  const cols = "xl:grid xl:grid-cols-[minmax(0,1fr)_190px_190px_200px_120px_96px] xl:items-center";

  return (
    <div className="flex flex-col lg:px-[13px]">
      <PageHero
        icon={ArrowRightLeft}
        title="Migrate to HostDock"
        description="Move a website from another hosting provider. Migrations run in the background: you can close this page and come back."
        action={<Link to="/migrate/new" className={primaryButtonClass}><Plus size={17} strokeWidth={2.2} />New migration</Link>}
      />

      {items && items.length > 0 && (
        <>
          <div className="mt-[10px] grid grid-cols-2 gap-[11px] xl:grid-cols-4">
            <StatCard icon={ArrowRightLeft} tile="teal" label="Migrations" value={q.data?.total ?? items.length} sub={(q.data?.total ?? 0) > items.length ? `Latest ${items.length} shown below` : "In your history"} />
            <StatCard icon={Loader2} tile="blue" label="In progress" value={count("running")} sub={count("running") ? "Running or waiting for you" : "Nothing running"} />
            <StatCard icon={CheckCircle2} tile="green" label="Completed" value={count("done")} sub={warnings ? `${warnings} with warnings` : count("done") ? "All items applied" : "None yet"} />
            <StatCard icon={XCircle} tile="orange" label="Failed or cancelled" value={count("failed")} sub={count("failed") ? "Open one to see why" : "None"} />
          </div>
          <Toolbar>
            <FilterTabs<Filter> label="Filter by status" value={filter} onChange={setFilter} items={[["all", `All (${items.length})`], ["running", `In progress (${count("running")})`], ["done", `Completed (${count("done")})`], ["failed", `Failed (${count("failed")})`]]} />
            <SearchBox value={search} onChange={setSearch} placeholder="Search by server or website..." />
          </Toolbar>
        </>
      )}

      <div className="mt-[15px]">
        {q.error ? (
          <div className="hd-card p-6"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></div>
        ) : q.isPending ? (
          <Skeleton className="h-40 w-full rounded-xl" />
        ) : !items || items.length === 0 ? (
          <EmptyCard icon={ScanSearch} title="No migrations yet" body="Choose the control panel your current host uses, sign in, and HostDock shows what it can move." action={<Link to="/migrate/new" className={primaryButtonClass}><ArrowRightLeft size={16} />Start a migration</Link>} />
        ) : rows && rows.length === 0 ? (
          <EmptyCard title="No matching migrations" body="Try a different search or filter." />
        ) : (
          <TableShell label="Migrations" cols={cols} headers={["Source", "Status", "Progress", "Destination", "Started", "Actions"]}>
            {rows?.map((m) => (
              <li key={m.id} role="row" aria-label={m.sourceHost} className={cn(cols, "flex flex-col gap-3 py-4 text-[12.5px] text-fg xl:min-h-[71px] xl:gap-0 xl:py-[11px]")}>
                <div role="cell" className="flex min-w-0 items-center gap-[14px] xl:pr-3">
                  <span className="flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-xl bg-[#d6f5f7] text-[#0a8ea0] dark:bg-surface-3"><Server size={21} strokeWidth={1.7} /></span>
                  <div className="min-w-0 leading-tight">
                    <span className="block truncate font-mono text-[13.5px] font-semibold" title={m.sourceHost}>{m.sourceHost}</span>
                    <span className="mt-[4px] block truncate text-[11.5px] text-muted">{m.providerLabel}{m.usernameHint ? ` · ${m.usernameHint}` : ""}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:contents">
                  <Cell label="Status" className={tableCell}>
                    <StatusPill status={m.status} />
                    {m.statusMessage && (m.status === "FAILED" || m.status === "COMPLETED_WITH_WARNINGS") && <span className="max-w-full truncate text-[11px] text-muted" title={m.statusMessage}>{m.statusMessage}</span>}
                  </Cell>
                  <Cell label="Progress" className={tableCell}><ProgressBar value={m.progress} tone={m.status === "FAILED" ? "danger" : FILTER.done(m.status) ? "success" : "accent"} /></Cell>
                  <Cell label="Destination" className={tableCell}>
                    {m.targetWebsite ? <Link to={`/websites/${m.targetWebsite.id}`} className="max-w-full truncate font-medium hover:text-accent" title={m.targetWebsite.domain}>{m.targetWebsite.domain}</Link> : <span className="text-muted">{m.status === "DISCOVERED" ? "Not chosen yet" : "—"}</span>}
                  </Cell>
                  <Cell label="Started" className={tableCell}>
                    <span title={formatDateTime(m.createdAt)}>{formatRelative(m.createdAt)}</span>
                    {m.finishedAt && <span className="text-[11px] text-subtle">Finished {formatRelative(m.finishedAt)}</span>}
                  </Cell>
                </div>
                <div role="cell" className={actionsCell}>
                  <button type="button" onClick={() => navigate(`/migrate/${m.id}`)} className={cn(rowPrimaryClass, "xl:w-[80px]")}><Eye size={15} className="shrink-0" />Open</button>
                </div>
              </li>
            ))}
          </TableShell>
        )}
      </div>

      <div className="hd-card mt-[19px] grid gap-[10px] p-[10px] sm:grid-cols-2 xl:grid-cols-4" aria-label="How migration works" role="list">
        {HOW.map((h) => (
          <div key={h.title} role="listitem" className="flex items-start gap-[14px] rounded-xl bg-surface-2/60 px-[14px] py-[14px]">
            <span className={cn("flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-xl dark:bg-surface-3", STAT_TILES[h.tile])}><h.icon size={21} strokeWidth={1.7} /></span>
            <span className="min-w-0 leading-snug">
              <span className="block text-[13.5px] font-bold text-fg">{h.title}</span>
              <span className="mt-[4px] block text-[12px] leading-[17px] text-muted">{h.body}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProgressBar({ value, tone = "accent", wide }: { value: number; tone?: "accent" | "danger" | "success"; wide?: boolean }) {
  return (
    <div className={cn("flex items-center gap-2", wide && "w-full")} role="progressbar" aria-label="Migration progress" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100}>
      <div className={cn("h-[7px] overflow-hidden rounded-full bg-surface-3", wide ? "flex-1" : "w-28")}><div className={cn("h-full rounded-full transition-all", tone === "danger" ? "bg-danger" : tone === "success" ? "bg-[#14a04a]" : "bg-[#048d9f]")} style={{ width: `${value}%` }} /></div>
      <span className="w-9 text-[12px] tabular-nums text-muted">{value}%</span>
    </div>
  );
}

// ---------------------------------------------------------------- wizard

const STEPS = ["Current host", "Connect", "Choose what to migrate"] as const;
const WP_STEPS = ["Current host", "Website address", "Admin sign-in"] as const;
/** Reached only from the WordPress source ("Use SFTP instead"), so the list shows one WordPress card. */
const WP_SFTP = "wordpress";
const WP_ADMIN = "wordpress-admin";

export function NewMigrationPage() {
  const navigate = useNavigate();
  const providers = useQuery({ queryKey: ["migration-providers"], queryFn: api.migrations.providers, staleTime: 5 * 60_000 });
  const [step, setStep] = useState(0);
  const [host, setHost] = useState("");
  const [port, setPort] = useState("");
  const [providerId, setProviderId] = useState("");
  const [creds, setCreds] = useState({ username: "", secret: "", extra: "" });
  const [approve, setApproveRaw] = useState<{ fingerprint256: string; subject: string | null; issuer: string | null; validTo: string | null } | null>(null);
  const [trusted, setTrusted] = useState(false);
  // Every new review starts unticked: a tick given for one certificate or key never carries over to another.
  const setApprove = (next: typeof approve) => { setTrusted(false); setApproveRaw(next); };
  /** The WordPress address typed for the admin-login route, kept while the customer tries the SFTP route. */
  const [wpAddress, setWpAddress] = useState("");

  const provider = providers.data?.items.find((p) => p.id === providerId);
  const portNum = port.trim() ? Number(port) : null;
  const wpAdmin = providerId === WP_ADMIN;
  const wordpress = wpAdmin || providerId === WP_SFTP;
  const panelKind = provider?.connection.kind === "panel";
  const steps = wordpress ? WP_STEPS : STEPS;
  /** A route offered from the "Other" or "not available yet" screens. */
  const pickRoute = (id: string) => { setProviderId(id); setHost(""); setPort(""); setCreds({ username: "", secret: "", extra: "" }); setStep(1); create.reset(); };
  /** WordPress: switch between the admin login (default) and an SFTP login, keeping what was typed where it still applies. */
  const switchWordPress = (to: string) => {
    if (to === WP_SFTP) {
      // The SFTP route needs a server address and the site's domain; the website address is kept for coming back.
      let domain = "";
      try { domain = new URL(/^[a-z]+:\/\//i.test(host.trim()) ? host.trim() : `https://${host.trim()}`).hostname.replace(/^www\./, ""); } catch { /* nothing typed */ }
      setWpAddress(host);
      setCreds({ username: "", secret: "", extra: domain });
      setHost(domain);
    } else {
      setCreds({ username: "", secret: "", extra: "" });
      setHost(wpAddress);
    }
    setPort("");
    setProviderId(to);
    setStep(1);
    create.reset();
  };

  const create = useMutation({
    mutationFn: (tlsFingerprint?: string) =>
      api.migrations.create({ provider: providerId as never, host: host.trim(), // No port: the API uses the panel's usual one.
        port: portNum, tlsFingerprint: tlsFingerprint ?? null, username: creds.username, secret: creds.secret, extra: creds.extra || undefined }),
    onSuccess: (m) => { setCreds({ username: "", secret: "", extra: "" }); navigate(`/migrate/${m.id}`); },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "TLS_APPROVAL_REQUIRED") setApprove(err.details as NonNullable<typeof approve>);
    },
  });

  const hostOk = host.trim().length > 2;
  const ready = Boolean(provider && provider.maturity !== "detect" && provider.credentialFields.filter((f) => f.required).every((f) => creds[f.name].trim()));

  return (
    <div className="flex flex-col lg:px-[13px]">
      <PageHero
        icon={ArrowRightLeft}
        title="Migrate to HostDock"
        description="Choose your current control panel, enter its address and sign in. Nothing is changed on your old host: HostDock only reads from it."
        action={<Link to="/migrate" className={secondaryButtonClass}><ArrowLeft size={15} />All migrations</Link>}
      />
      <ol className="hd-card mt-[10px] grid grid-cols-3 gap-2 px-[18px] py-[14px]" aria-label="Steps">
        {steps.map((s, i) => (
          <li key={s} className="flex min-w-0 items-center gap-[10px]" aria-current={i === step ? "step" : undefined}>
            <span className={cn("flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full text-[12.5px] font-semibold tabular-nums",
              i < step ? "bg-[#048d9f] text-white" : i === step ? "bg-[#d6f5f7] text-[#0a8ea0] ring-2 ring-[#048d9f] dark:bg-accent-soft dark:text-accent" : "bg-surface-3 text-muted")}>
              {i < step ? <CheckCircle2 size={16} /> : i + 1}
            </span>
            <span className="min-w-0 leading-tight">
              <span className="block text-[10.5px] tracking-wide text-subtle uppercase max-sm:hidden">Step {i + 1}</span>
              <span className={cn("block truncate text-[13px]", i === step ? "font-semibold text-fg" : i < step ? "font-medium text-[#0a8ea0] dark:text-accent" : "text-muted")}>{s}</span>
            </span>
            {i < steps.length - 1 && <span className={cn("ml-1 hidden h-[2px] flex-1 rounded-full md:block", i < step ? "bg-[#048d9f]" : "bg-line")} aria-hidden />}
          </li>
        ))}
      </ol>
      <div className="mt-[15px] grid items-start gap-[15px] xl:grid-cols-[minmax(0,1fr)_340px]">
      <DetailCard title={["Where is your website currently hosted?",
        providerId === OTHER ? "Other / Manual migration" : wpAdmin ? "What is your WordPress website's address?" : provider?.id === WP_SFTP ? "Where can HostDock sign in over SFTP?" : `Connect your ${provider?.label ?? "control panel"} account`,
        wpAdmin ? "Sign in as a WordPress administrator" : `Sign in to ${provider?.label ?? "your old host"}`][step]}>
        {step === 0 && (
          <div className="flex flex-col gap-3">
            <p className="text-[12.5px] text-muted">Pick the control panel you sign in to today (its sign-in page usually shows the name). For a WordPress site on any other host, choose WordPress site: all you need is its address and an administrator login.</p>
            {providers.isPending && <Skeleton className="h-40 w-full rounded-xl" />}
            {providers.error && <ErrorState error={providers.error} onRetry={() => void providers.refetch()} />}
            {providers.data && <SourcePicker providers={providers.data.items} value={providerId} onPick={setProviderId} />}
            {provider && provider.capabilities.length > 0 && <CapabilityList caps={provider.capabilities} />}
            {provider && provider.notes.length > 0 && <ul className="list-disc space-y-1 pl-5 text-[12.5px] text-muted">{provider.notes.map((n) => <li key={n}>{n}</li>)}</ul>}
            <div className="flex justify-between border-t border-line pt-4">
              <Button variant="ghost" onClick={() => navigate("/migrate")}>Cancel</Button>
              <Button variant="primary" disabled={!provider && providerId !== OTHER} onClick={() => setStep(1)}>Continue</Button>
            </div>
          </div>
        )}
        {step === 1 && providerId === OTHER && <OtherSource onPick={pickRoute} onBack={() => setStep(0)} />}
        {step === 1 && provider && panelKind && provider.support === "NOT_IMPLEMENTED" && <UnavailableSource provider={provider} onPick={pickRoute} onBack={() => setStep(0)} />}
        {step === 1 && provider && panelKind && provider.support !== "NOT_IMPLEMENTED" && (
          <PanelConnect key={provider.id} provider={provider} address={host} onAddress={setHost} onSwitch={(id) => setProviderId(id)} onBack={() => setStep(0)} />
        )}
        {step === 1 && provider && wordpress && (
          <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); if (hostOk) setStep(2); }}>
            {wpAdmin ? (
              <Field label="Website address" hint="The address visitors type, e.g. https://example.com (or https://example.com/blog when WordPress lives in a folder).">
                {(p) => <Input {...p} value={host} onChange={(e) => setHost(e.target.value)} placeholder="https://example.com" autoFocus autoComplete="off" spellCheck={false} inputMode="url" />}
              </Field>
            ) : (<>
            <Field label="Old server address" hint="The SFTP (SSH) server your current host gives you, often your domain or a name like server123.host.com.">
              {(p) => <Input {...p} value={host} onChange={(e) => setHost(e.target.value)} placeholder="server.example.com" autoFocus autoComplete="off" spellCheck={false} />}
            </Field>
            <Field label="Port (optional)" hint="Leave empty for 22. Some hosts use another SFTP port: Hostinger 65002, SiteGround 18765, Namecheap 21098, A2 Hosting 7822.">
              {(p) => <Input {...p} inputMode="numeric" value={port} onChange={(e) => setPort(e.target.value.replace(/\D/g, ""))} placeholder={provider.defaultPorts[0] ? String(provider.defaultPorts[0]) : undefined} className="max-w-[8rem]" />}
            </Field>
            </>)}
            <WordPressSwitch providerId={providerId} onSwitch={switchWordPress} />
            <div className="flex justify-between border-t border-line pt-4">
              <Button variant="ghost" onClick={() => (provider.id === WP_SFTP ? switchWordPress(WP_ADMIN) : setStep(0))}>Back</Button>
              <Button variant="primary" type="submit" disabled={!hostOk}>Continue</Button>
            </div>
          </form>
        )}
        {step === 2 && provider && wordpress && (
          <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); if (ready) create.mutate(undefined); }} autoComplete="off">
            <Notice title="Your credentials are protected">
              They are sent over HTTPS, encrypted at rest, used only for this migration, never shown again, and permanently deleted when it finishes, fails for good, is cancelled, or after 24 hours.
            </Notice>
            {provider.credentialFields.map((f) => (
              <Field key={f.name} label={f.label} hint={f.hint}>
                {(p) => f.type === "textarea"
                  ? <Textarea {...p} rows={4} value={creds[f.name]} onChange={(e) => setCreds({ ...creds, [f.name]: e.target.value })} spellCheck={false} autoComplete="off" />
                  : <Input {...p} type={f.type} value={creds[f.name]} onChange={(e) => setCreds({ ...creds, [f.name]: e.target.value })} spellCheck={false} autoComplete={f.type === "password" ? "new-password" : "off"} />}
              </Field>
            ))}
            {create.error && !(create.error instanceof ApiError && create.error.code === "TLS_APPROVAL_REQUIRED") && <FormError error={create.error} />}
            <WordPressSwitch providerId={providerId} onSwitch={switchWordPress} />
            <div className="flex justify-between border-t border-line pt-4">
              <Button variant="ghost" onClick={() => setStep(1)}>Back</Button>
              <Button variant="primary" type="submit" loading={create.isPending} disabled={!ready} icon={<Lock size={14} />}>Connect securely</Button>
            </div>
          </form>
        )}
      </DetailCard>
      <aside className="hd-card px-[18px] py-[16px]" aria-label="What to expect">
        <h2 className="text-[14.5px] font-bold text-fg">What to expect</h2>
        <ul className="mt-[12px] flex flex-col gap-[14px]">
          {HOW.map((h) => (
            <li key={h.title} className="flex items-start gap-[12px]">
              <span className={cn("flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-xl dark:bg-surface-3", STAT_TILES[h.tile])}><h.icon size={18} strokeWidth={1.8} /></span>
              <span className="min-w-0 leading-snug"><span className="block text-[13px] font-semibold text-fg">{h.title}</span><span className="mt-[2px] block text-[12px] leading-[17px] text-muted">{h.body}</span></span>
            </li>
          ))}
        </ul>
      </aside>
      </div>

      <Dialog
        open={approve !== null}
        onOpenChange={(o) => { if (!o) setApprove(null); }}
        title="Review this server's certificate"
        description={wpAdmin
            ? "Your website uses a certificate that is not issued by a public authority, so browsers would warn about it too. Only continue if you know why (for example a test site), and check the fingerprint with your host."
            : "This server uses a certificate that is not issued by a public authority (common for control panels). Check the fingerprint against what your old host shows before trusting it."}
        footer={<><Button variant="ghost" onClick={() => setApprove(null)}>Cancel</Button><Button variant="primary" disabled={!trusted} loading={create.isPending} onClick={() => { const fp = approve!.fingerprint256; setApprove(null); create.mutate(fp); }}>Trust and connect</Button></>}
      >
        {approve && (
          <div className="flex flex-col gap-4">
            <KeyValue items={[{ label: "Issued to", value: approve.subject ?? "—" }, { label: "Issued by", value: approve.issuer ?? "—" }, { label: "Valid until", value: approve.validTo ?? "—" }, { label: "SHA-256 fingerprint", value: <span className="break-all font-mono text-[12px]">{approve.fingerprint256}</span> }]} />
            <Checkbox checked={trusted} onChange={setTrusted} label="I recognise this server and trust this certificate for this migration only" />
          </div>
        )}
      </Dialog>
    </div>
  );
}

/** The two ways in for a WordPress site: its admin login (default) or, when that is blocked, an SFTP login. */
function WordPressSwitch({ providerId, onSwitch }: { providerId: string; onSwitch: (to: string) => void }) {
  if (providerId !== WP_ADMIN && providerId !== WP_SFTP) return null;
  return (
    <p className="text-[12.5px] text-muted">
      {providerId === WP_ADMIN
        ? <>Two-step login, a CAPTCHA, or plugin uploads turned off? <button type="button" className="font-medium text-accent hover:underline" onClick={() => onSwitch(WP_SFTP)}>Use an SFTP login instead</button></>
        : <>Easier: <button type="button" className="font-medium text-accent hover:underline" onClick={() => onSwitch(WP_ADMIN)}>use your WordPress admin login instead</button></>}
    </p>
  );
}

// ---------------------------------------------------------------- detail

interface Report {
  summary?: { selected: number; done: number; skipped: number; failed: number };
  checks?: { name: string; ok: boolean; detail: string }[];
  destination?: { domain: string };
  dns?: { domain: string; type: string; value: string; note: string }[];
  notMigrated?: { kind: string; name: string; reason: string | null }[];
  nextSteps?: string[];
  files?: { domain: string; files: number; bytes?: number }[];
  databases?: { source: string; engine: string; tables: number }[];
}

export function MigrationDetailPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["migration", id],
    queryFn: () => api.migrations.get(id),
    refetchInterval: (s) => (s.state.data && (ACTIVE.includes(s.state.data.status)) ? 2000 : false),
  });
  const refresh = (m: MigrationDetail) => { qc.setQueryData(["migration", id], m); void qc.invalidateQueries({ queryKey: ["migrations"] }); };
  const cancel = useMutation({ meta: { inlineError: true }, mutationFn: () => api.migrations.cancel(id), onSuccess: (m) => { refresh(m); toast.success("Cancelling the migration"); } });
  const retry = useMutation({ mutationFn: () => api.migrations.retry(id), onSuccess: (m) => { refresh(m); toast.success("Retrying"); } });
  const navigate = useNavigate();
  const remove = useMutation({ meta: { inlineError: true }, mutationFn: () => api.migrations.remove(id), onSuccess: () => { void qc.invalidateQueries({ queryKey: ["migrations"] }); navigate("/migrate"); } });
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (q.error) return <div className="hd-card p-6 lg:mx-[13px]"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></div>;
  const m = q.data;
  if (!m) return <Skeleton className="h-80 w-full rounded-xl" />;
  const active = ACTIVE.includes(m.status);
  const report = m.report as Report;

  const canRetry = (m.status === "FAILED" || (m.status === "COMPLETED_WITH_WARNINGS" && m.resources.some((r) => r.status === "FAILED"))) && m.credentialsStored;

  return (
    <div className="flex flex-col lg:px-[13px]">
      <PageHero
        icon={ArrowRightLeft}
        title={`${m.providerLabel} · ${m.sourceHost}`}
        description={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Link to="/migrate" className="inline-flex items-center gap-1 font-medium text-accent hover:underline"><ArrowLeft size={13} />All migrations</Link>
            <StatusPill status={m.status} />
            {m.statusMessage && <span className="min-w-0">{m.statusMessage}</span>}
          </span>
        }
        action={
          <div className="flex flex-wrap gap-2">
            {(active || m.status === "DISCOVERED") && <button type="button" onClick={() => setConfirmCancel(true)} className={secondaryButtonClass}><XCircle size={15} />Cancel migration</button>}
            {!active && <button type="button" onClick={() => setConfirmDelete(true)} className={cn(secondaryButtonClass, "text-danger")}><Trash2 size={15} />Delete</button>}
            {canRetry && <button type="button" disabled={retry.isPending} onClick={() => retry.mutate()} className={primaryButtonClass}>{retry.isPending ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />}Retry failed items</button>}
          </div>
        }
      />
      {((active && m.status !== "DRAFT" && m.status !== "DISCOVERING") || TERMINAL.includes(m.status)) && <MigrationStats m={m} />}
      <div className="mt-[15px] flex flex-col gap-[15px]">
        <FormError error={cancel.error ?? retry.error} />
        {(m.status === "DRAFT" || m.status === "DISCOVERING") && <Working title="Connecting and reading your account" detail="This usually takes under a minute. It is safe to leave this page." />}
        {m.status === "DISCOVERED" && (m.accountPlan ? <AccountSelectionStep m={m} onChange={refresh} /> : <SelectionStep m={m} onChange={refresh} />)}
        {active && m.status !== "DRAFT" && m.status !== "DISCOVERING" && <RunProgress m={m} />}
        {m.status === "FAILED" && <Notice tone="danger" title="This migration failed">{m.statusMessage}{m.credentialsStored ? " Your stored credentials will be deleted automatically when this migration expires." : " The stored credentials were deleted."}</Notice>}
        {m.status === "CANCELLED" && <Notice title="Cancelled">Nothing more will be copied. The stored credentials were deleted. Items already applied were kept.</Notice>}
        {TERMINAL.includes(m.status) && report.summary && <Report m={m} report={report} />}
        {TERMINAL.includes(m.status) && report.summary && m.accountPlan && <AccountReportExtras m={m} onChange={refresh} />}
        <ActivityLog m={m} />
      </div>
      <ConfirmDialog open={confirmCancel} onOpenChange={setConfirmCancel} title="Cancel this migration?" description="Copying stops at the next safe point and the stored credentials are deleted. Items already applied stay on your website." confirmLabel="Cancel migration" loading={cancel.isPending} error={cancel.error} onConfirm={() => cancel.mutate(undefined, { onSuccess: () => setConfirmCancel(false) })} />
      <ConfirmDialog open={confirmDelete} onOpenChange={setConfirmDelete} title="Delete this migration record?" description="Removes it from your history. Your websites are not affected." confirmLabel="Delete" loading={remove.isPending} error={remove.error} onConfirm={() => remove.mutate()} />
    </div>
  );
}

/** Totals for a migration that has started: what was picked, and how far it got. */
function MigrationStats({ m }: { m: MigrationDetail }) {
  const picked = m.resources.filter((r) => r.selected);
  const done = picked.filter((r) => r.status === "DONE").length;
  const failed = picked.filter((r) => r.status === "FAILED").length;
  const bytes = picked.reduce((a, r) => a + (r.bytesTotal ?? 0), 0);
  return (
    <div className="mt-[10px] grid grid-cols-2 gap-[11px] xl:grid-cols-4">
      <StatCard icon={ListChecks} tile="teal" label="Items selected" value={picked.length} sub={bytes ? `${formatBytes(bytes)} to copy` : "Chosen from your old host"} />
      <StatCard icon={CheckCircle2} tile="green" label="Applied" value={done} sub={picked.length ? `${Math.round((done / picked.length) * 100)}% of the selection` : undefined} />
      <StatCard icon={XCircle} tile="orange" label="Failed" value={failed} sub={failed ? "See the reason below" : "Nothing failed"} />
      <StatCard icon={Clock} tile="blue" label={m.finishedAt ? "Finished" : "Started"} value={formatRelative(m.finishedAt ?? m.startedAt ?? m.createdAt)} sub={m.targetWebsite ? `Into ${m.targetWebsite.domain}` : undefined} />
    </div>
  );
}

function Working({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="hd-card flex items-center gap-[16px] px-[18px] py-[18px]" aria-live="polite">
      <span className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-xl bg-[#d6f5f7] text-[#0a8ea0] dark:bg-accent-soft dark:text-accent"><Loader2 size={22} className="animate-spin" /></span>
      <div className="leading-tight"><p className="text-[14.5px] font-bold">{title}</p><p className="mt-[4px] text-[12.5px] text-muted">{detail}</p></div>
    </div>
  );
}

function SelectionStep({ m, onChange }: { m: MigrationDetail; onChange(m: MigrationDetail): void }) {
  const sites = useQuery({ queryKey: ["websites", "all"], queryFn: () => api.websites.list({ pageSize: 100 }) });
  const active = (sites.data?.items ?? []).filter((w) => w.status === "ACTIVE");
  const movable = m.resources.filter((r) => r.transferable);
  const [picked, setPicked] = useState<Set<string>>(() => new Set(movable.map((r) => r.id)));
  const [target, setTarget] = useState(m.targetWebsite?.id ?? "");
  const [confirm, setConfirm] = useState(false);
  const [filesMode, setFilesMode] = useState<"merge" | "replace">(m.options.filesMode);
  const pickedItems = m.resources.filter((r) => picked.has(r.id));
  const needsTransfer = pickedItems.some((r) => r.requires.includes("TRANSFER_ACCESS"));
  const needDbs = pickedItems.filter((r) => r.requires.includes("DB_CREDENTIALS") && !m.access.databases[r.name]);
  const accessReady = (!needsTransfer || m.access.transfer !== null) && needDbs.length === 0;
  const hasFiles = pickedItems.some((r) => r.kind === "FILES");
  useEffect(() => { if (!target && active.length === 1) setTarget(active[0]!.id); }, [active, target]);
  const groups = useMemo(() => KIND_ORDER.map((k) => ({ kind: k, items: m.resources.filter((r) => r.kind === k) })).filter((g) => g.items.length), [m.resources]);
  const start = useMutation({ meta: { inlineError: true },
    mutationFn: async () => { await api.migrations.select(m.id, { resourceIds: [...picked], targetWebsiteId: target, filesMode }); return api.migrations.start(m.id); },
    onSuccess: (r) => { setConfirm(false); onChange(r); },
  });
  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const nothing = movable.length === 0;

  return (
    <>
      <Notice tone="success" title="Connected">
        Signed in as {String((m.account as { username?: string }).username ?? m.usernameHint)}. Below is everything HostDock found. Items that cannot be moved yet are shown with the reason, so nothing is silently left behind.
      </Notice>
      {m.warnings.map((w) => <Notice key={w} tone="warning">{w}</Notice>)}
      {m.diagnostics.length > 0 && (
        <Notice tone="warning" title="Some parts of your account could not be read">
          <ul className="list-disc space-y-0.5 pl-5">{m.diagnostics.map((d) => <li key={`${d.group}-${d.code}`}><span className="font-medium">{d.group}:</span> {d.message}</li>)}</ul>
        </Notice>
      )}
      {nothing && <Notice tone="warning" title="Nothing can be moved automatically yet">HostDock found your account but none of its items can be migrated by this adapter in this release. You can still use the list below as a checklist.</Notice>}
      {groups.map((g) => (
        <DetailCard key={g.kind} title={`${KIND_LABEL[g.kind] ?? g.kind} (${g.items.length})`} flush>
          <ul className="divide-y divide-line">
            {g.items.map((r) => <ResourceRow key={r.id} r={r} checked={picked.has(r.id)} onToggle={() => toggle(r.id)} />)}
          </ul>
        </DetailCard>
      ))}
      {!nothing && (needsTransfer || needDbs.length > 0 || Object.keys(m.access.databases).length > 0) && (
        <AccessPanel m={m} needsTransfer={needsTransfer} databases={pickedItems.filter((r) => r.requires.includes("DB_CREDENTIALS")).map((r) => r.name)} onChange={onChange} />
      )}
      {!nothing && (
        <DetailCard title="Destination">
          <Field label="Put the imported items on this website" hint="Domains are added to it as aliases, and cron jobs are added paused so you can review them first.">
            {(p) => <Select {...p} value={target} onChange={(e) => setTarget(e.target.value)}><option value="">Choose a website…</option>{active.map((w) => <option key={w.id} value={w.id}>{w.primaryDomain}</option>)}</Select>}
          </Field>
          {hasFiles && (
            <Field label="If a file already exists on the destination" hint="Merge keeps existing files and overwrites only files with the same path. Replace empties the website's files folder first.">
              {(p) => <Select {...p} value={filesMode} onChange={(e) => setFilesMode(e.target.value as "merge" | "replace")}><option value="merge">Merge with existing files</option><option value="replace">Replace existing files</option></Select>}
            </Field>
          )}
          {!accessReady && <Notice tone="warning">Sign-in details are needed for the selected items. Fill in the section above, or untick those items.</Notice>}
          {sites.data && active.length === 0 && <Notice tone="warning">You need an active website first. <Link className="underline" to="/websites/new">Create one</Link>, then come back.</Notice>}
          <div className="mt-4 flex justify-end"><Button variant="primary" disabled={!target || picked.size === 0 || !accessReady} onClick={() => setConfirm(true)}>Review and start</Button></div>
        </DetailCard>
      )}
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title="Ready to migrate"
        tone="primary"
        confirmLabel="Start migration"
        loading={start.isPending}
        error={start.error}
        description="Check the summary, then start. The migration runs in the background."
        onConfirm={() => start.mutateAsync()}
      >
        <ReadySummary m={m} items={pickedItems} destination={active.find((w) => w.id === target)?.primaryDomain ?? "your website"} />
        <ul className="list-disc space-y-1 pl-5 text-[12.5px] text-muted">
          <li>Your old host is only read from, never changed.</li>
          {filesMode === "replace" && hasFiles && <li className="font-medium text-danger">Existing files on the destination website will be replaced.</li>}
          <li>Imported domains need their DNS changed before they go live; the report will tell you exactly what.</li>
          <li>Imported cron jobs stay paused until you enable them.</li>
        </ul>
      </ConfirmDialog>
    </>
  );
}

/** "Ready to migrate": what will be moved, from where to where, before anything starts. No finish time is promised. */
export function ReadySummary({ m, items, destination }: { m: MigrationDetail; items: MigrationResourceRecord[]; destination: string }) {
  const counts = KIND_ORDER.map((k) => ({ k, n: items.filter((r) => r.kind === k).length })).filter((x) => x.n > 0);
  const sized = items.filter((r) => r.bytesTotal !== null);
  const bytes = sized.reduce((a, r) => a + (r.bytesTotal ?? 0), 0);
  return (
    <div className="flex flex-col gap-3">
      <KeyValue items={[
        { label: "Source", value: `${m.providerLabel} · ${m.sourceHost}` },
        { label: "Destination", value: destination },
        { label: "Items", value: counts.length ? counts.map((c) => `${c.n} ${(KIND_LABEL[c.k] ?? c.k).toLowerCase()}`).join(", ") : "—" },
        { label: "Estimated size", value: sized.length === 0 ? "Not reported by the old host" : `${formatBytes(bytes)}${sized.length < items.length ? " (not every item reports its size)" : ""}` },
      ]} />
      <p className="text-[12px] text-muted">How long it takes depends on the amount of data and the old server's speed; progress is shown live and you can close this page.</p>
    </div>
  );
}

function AccessPanel({ m, needsTransfer, databases, onChange }: { m: MigrationDetail; needsTransfer: boolean; databases: string[]; onChange(m: MigrationDetail): void }) {
  return (
    <DetailCard title="Sign-in for the transfer" description="HostDock needs these only to copy your data. They are stored encrypted, never shown again, and deleted when the migration finishes.">
      {needsTransfer && <TransferAccessForm m={m} onChange={onChange} />}
      {databases.length > 0 && <DatabaseAccessForms m={m} names={databases} onChange={onChange} />}
    </DetailCard>
  );
}

export function TransferAccessForm({ m, onChange }: { m: MigrationDetail; onChange(m: MigrationDetail): void }) {
  const saved = m.access.transfer;
  const [protocol, setProtocol] = useState<"sftp" | "ftps" | "ftp">(saved?.protocol ?? "sftp");
  const [port, setPort] = useState(saved?.port ? String(saved.port) : "");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [allowPlain, setAllowPlain] = useState(false);
  const portNum = port.trim() ? Number(port) : null;
  // SFTP: HostDock reads the server's SSH key when this is saved and accepts only that key for the rest of the migration.
  const save = useMutation({ meta: { inlineError: true },
    mutationFn: () => api.migrations.setAccess(m.id, {
      transfer: {
        protocol, port: portNum, username, ...(password ? { password } : {}), ...(protocol === "sftp" && privateKey ? { privateKey } : {}),
        ...(protocol === "ftp" ? { allowPlainFtp: allowPlain } : {}),
      },
    }),
    onSuccess: (r) => { setPassword(""); setPrivateKey(""); onChange(r); toast.success("Transfer sign-in saved"); },
  });
  const remove = useMutation({ mutationFn: () => api.migrations.setAccess(m.id, { transfer: null }), onSuccess: (r) => onChange(r) });
  const ready = username.trim() && (password || (protocol === "sftp" && privateKey)) && (protocol !== "ftp" || allowPlain);
  return (
    <div className="space-y-3">
      <h3 className="text-[13px] font-semibold">Website files</h3>
      {saved && <Notice tone="success">Saved for {saved.usernameHint} over {saved.protocol.toUpperCase()}. Enter new details below to replace them. <Button size="sm" variant="ghost" loading={remove.isPending} onClick={() => remove.mutate()}>Remove</Button></Notice>}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Protocol" hint="SFTP is recommended.">{(p) => <Select {...p} value={protocol} onChange={(e) => setProtocol(e.target.value as typeof protocol)}><option value="sftp">SFTP (SSH)</option><option value="ftps">FTPS (FTP over TLS)</option><option value="ftp">FTP (not encrypted)</option></Select>}</Field>
        <Field label="Port" hint="Leave empty for the default.">{(p) => <Input {...p} inputMode="numeric" value={port} onChange={(e) => setPort(e.target.value.replace(/\D/g, ""))} placeholder={protocol === "sftp" ? "22" : "21"} />}</Field>
        <Field label="Username">{(p) => <Input {...p} autoComplete="off" value={username} onChange={(e) => setUsername(e.target.value)} />}</Field>
        <Field label="Password">{(p) => <Input {...p} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />}</Field>
      </div>
      {protocol === "sftp" && (
        <>
          <Field label="Private key (optional)" hint="Used instead of the password if you paste one.">{(p) => <Textarea {...p} rows={3} spellCheck={false} autoComplete="off" value={privateKey} onChange={(e) => setPrivateKey(e.target.value)} />}</Field>
        </>
      )}
      {protocol === "ftp" && <Checkbox checked={allowPlain} onChange={() => setAllowPlain((v) => !v)} label="I understand plain FTP sends my password and files unencrypted" />}
      <FormError error={save.error} />
      <div className="flex justify-end"><Button variant="primary" disabled={!ready} loading={save.isPending} onClick={() => save.mutate()}>Save sign-in</Button></div>
    </div>
  );
}

function DatabaseAccessForms({ m, names, onChange }: { m: MigrationDetail; names: string[]; onChange(m: MigrationDetail): void }) {
  return (
    <div className="mt-5 space-y-3">
      <h3 className="text-[13px] font-semibold">Databases</h3>
      <p className="text-[12.5px] text-muted">Your old panel does not let HostDock create a temporary database login, so enter an existing one for each database.</p>
      {names.map((n) => <DatabaseAccessRow key={n} m={m} name={n} onChange={onChange} />)}
    </div>
  );
}

function DatabaseAccessRow({ m, name, onChange }: { m: MigrationDetail; name: string; onChange(m: MigrationDetail): void }) {
  const saved = m.access.databases[name];
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const save = useMutation({ mutationFn: () => api.migrations.setAccess(m.id, { databases: { [name]: { username, password } } }), onSuccess: (r) => { setPassword(""); onChange(r); } });
  return (
    <div className="rounded-md border border-line p-3">
      <p className="text-[13px] font-medium">{name} {saved && <Badge tone="success">Saved for {saved.usernameHint}</Badge>}</p>
      <div className="mt-2 grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <Field label="Database username">{(p) => <Input {...p} autoComplete="off" value={username} onChange={(e) => setUsername(e.target.value)} />}</Field>
        <Field label="Database password">{(p) => <Input {...p} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />}</Field>
        <Button disabled={!username.trim() || !password} loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
      </div>
      <FormError error={save.error} />
    </div>
  );
}

export function ResourceRow({ r, checked, onToggle }: { r: MigrationResourceRecord; checked: boolean; onToggle(): void }) {
  return (
    <li className="flex items-start gap-3 px-4 py-2.5">
      {r.transferable
        ? <Checkbox checked={checked} onChange={onToggle} label={<span className="font-medium">{r.name}</span>} />
        : <div className="min-w-0 flex-1"><p className="text-[13.5px] font-medium text-muted">{r.name}</p>{r.unavailableReason && <p className="text-[12px] text-subtle">{r.unavailableReason}</p>}</div>}
      <div className="ml-auto flex shrink-0 items-center gap-2 text-right">
        {r.bytesTotal != null && <span className="text-[12px] tabular-nums text-muted">{formatBytes(r.bytesTotal)}</span>}
        {!r.transferable && <Badge tone="neutral">Not migrated yet</Badge>}
      </div>
    </li>
  );
}

const STAGES: { label: string; statuses: MigrationStatus[] }[] = [
  { label: "Queued", statuses: ["QUEUED"] }, { label: "Connecting", statuses: ["CONNECTING"] },
  { label: "Migrating", statuses: ["EXPORTING", "TRANSFERRING", "RESTORING"] }, { label: "Verifying", statuses: ["VALIDATING"] },
];

function RunProgress({ m }: { m: MigrationDetail }) {
  const current = STAGES.findIndex((s) => s.statuses.includes(m.status));
  const selected = m.resources.filter((r) => r.selected);
  return (
    <DetailCard title="Migration in progress" description="Running on HostDock's servers. You can safely close this page.">
      <ol className="mb-4 flex flex-wrap gap-x-5 gap-y-2" aria-label="Stages">
        {STAGES.map((s, i) => (
          <li key={s.label} className={cn("flex items-center gap-1.5 text-[13px]", i === current ? "font-semibold" : i < current ? "text-accent" : "text-subtle")}>
            {i < current ? <CheckCircle2 size={14} /> : i === current ? <Loader2 size={14} className="animate-spin" /> : <span className="h-3.5 w-3.5 rounded-full border border-line-strong" />}{s.label}
          </li>
        ))}
      </ol>
      <ProgressBar value={m.progress} wide />
      <ul className="mt-4 divide-y divide-line">
        {selected.map((r) => (
          <li key={r.id} className="flex items-center justify-between gap-3 py-2 text-[13px]">
            <span className="min-w-0 truncate"><span className="text-muted">{KIND_LABEL[r.kind] ?? r.kind}: </span>{r.name}</span>
            <ResourceState r={r} />
          </li>
        ))}
      </ul>
    </DetailCard>
  );
}

function ResourceState({ r }: { r: MigrationResourceRecord }) {
  if (r.status === "DONE") return <span className="flex items-center gap-1 text-success"><CheckCircle2 size={14} />Done</span>;
  if (r.status === "SKIPPED") return <span className="text-muted" title={r.error ?? undefined}>Skipped</span>;
  if (r.status === "FAILED") return <span className="flex items-center gap-1 text-danger" title={r.error ?? undefined}><XCircle size={14} />Failed</span>;
  if (r.status === "RUNNING") return <span className="flex items-center gap-1 text-accent"><Loader2 size={14} className="animate-spin" />{r.progress}%</span>;
  return <span className="text-subtle">Pending</span>;
}

function Report({ m, report }: { m: MigrationDetail; report: Report }) {
  const s = report.summary!;
  const items = m.resources.filter((r) => r.selected);
  const unmigrated = report.notMigrated ?? [];
  const byKind = KIND_ORDER.map((k) => ({ k, rows: unmigrated.filter((u) => u.kind === k) })).filter((g) => g.rows.length);
  return (
    <>
      <DetailCard title="Migration report" description={`Finished ${formatDateTime(m.finishedAt)}`}>
        <KeyValue items={[
          { label: "Applied", value: `${s.done} of ${s.selected}` }, { label: "Skipped", value: s.skipped }, { label: "Failed", value: s.failed },
          { label: "Destination", value: report.destination?.domain ?? "—" },
        ]} />
        {report.checks && report.checks.length > 0 && (
          <ul className="mt-4 space-y-1.5">
            {report.checks.map((c) => <li key={c.name} className="flex items-center gap-2 text-[13px]">{c.ok ? <ShieldCheck size={14} className="text-success" /> : <XCircle size={14} className="text-danger" />}<span className="font-medium">{c.name}</span><span className="text-muted">{c.detail}</span></li>)}
          </ul>
        )}
        <ul className="mt-4 divide-y divide-line">
          {items.map((r) => <li key={r.id} className="flex items-center justify-between gap-3 py-2 text-[13px]"><span className="min-w-0"><span className="text-muted">{KIND_LABEL[r.kind] ?? r.kind}: </span>{r.name}{r.error && <span className="block text-[12px] text-muted">{r.error}</span>}</span><ResourceState r={r} /></li>)}
        </ul>
      </DetailCard>
      {report.nextSteps && report.nextSteps.length > 0 && (
        <DetailCard title="What to do next">
          <ul className="list-disc space-y-1.5 pl-5 text-[13px]">{report.nextSteps.map((n) => <li key={n}>{n}</li>)}</ul>
        </DetailCard>
      )}
      {report.dns && report.dns.length > 0 && (
        <DetailCard title="Recommended DNS changes" description="Switch these at your DNS provider when you are ready. Until then your old site keeps working.">
          <ul className="divide-y divide-line">
            {report.dns.map((d) => (
              <li key={d.domain} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-[13px]">
                <span><span className="font-medium">{d.domain}</span> <span className="text-muted">→ {d.type} record</span></span>
                <span className="flex items-center gap-2 font-mono text-[12.5px]">{d.value}<CopyButton value={d.value} /></span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[12.5px] text-muted">HTTPS certificates are issued automatically once each domain points to HostDock.</p>
        </DetailCard>
      )}
      {byKind.length > 0 && (
        <DetailCard title="Not migrated" description="HostDock cannot move these automatically yet. Plan to move them by hand before you cancel your old hosting.">
          <ul className="space-y-3">
            {byKind.map((g) => (
              <li key={g.k}>
                <p className="text-[13px] font-medium">{KIND_LABEL[g.k] ?? g.k} ({g.rows.length})</p>
                <p className="text-[12.5px] text-muted">{g.rows[0]!.reason ?? "Not supported yet"}</p>
                <p className="mt-0.5 line-clamp-2 text-[12px] text-subtle">{g.rows.slice(0, 8).map((r) => r.name).join(", ")}{g.rows.length > 8 ? `, +${g.rows.length - 8} more` : ""}</p>
              </li>
            ))}
          </ul>
        </DetailCard>
      )}
    </>
  );
}

function ActivityLog({ m }: { m: MigrationDetail }) {
  const [open, setOpen] = useState(false);
  if (!m.logs.length) return null;
  return (
    <DetailCard title="Activity log" actions={<Button size="sm" variant="ghost" onClick={() => setOpen(!open)}>{open ? "Hide" : "Show"}</Button>}>
      {open ? (
        <ol className="max-h-80 space-y-1 overflow-y-auto font-mono text-[12px]">
          {m.logs.map((l) => <li key={l.id} className={cn("flex gap-2", l.level === "ERROR" ? "text-danger" : l.level === "WARN" ? "text-warning" : "text-muted")}><span className="shrink-0 text-subtle">{formatDateTime(l.at)}</span><span className="shrink-0">[{l.step}]</span><span className="min-w-0 break-words">{l.message}</span></li>)}
        </ol>
      ) : <p className="text-[12.5px] text-muted">{m.logs.length} entries. Credentials never appear in this log.</p>}
    </DetailCard>
  );
}
